import { useEffect, useState } from 'react';
import type { Customer, Quote, QuoteStatus } from '../types';

interface ShellProps { children: React.ReactNode; onClose: () => void; maxWidth?: number }
function Shell({ children, onClose, maxWidth }: ShellProps) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" style={maxWidth ? { maxWidth } : undefined} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

interface AddQuoteProps {
  customers: Customer[];
  defaultCustomerId: string | null;
  onCancel: () => void;
  onSave: (data: { customerId: string; clarityRef: string; notes: string; revenue: number }) => void;
}

export function QuoteModal({ customers, defaultCustomerId, onCancel, onSave }: AddQuoteProps) {
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? customers[0]?.id ?? '');
  const [clarityRef, setClarityRef] = useState('');
  const [notes, setNotes] = useState('');
  const [revenue, setRevenue] = useState('');

  useEffect(() => {
    if (!customerId && customers[0]) setCustomerId(customers[0].id);
  }, [customers, customerId]);

  return (
    <Shell onClose={onCancel} maxWidth={480}>
      <div className="modal-title">New <span>Quote</span></div>
      <div className="modal-field">
        <label>Customer</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="modal-field">
        <label>Clarity Reference *</label>
        <input
          type="text" value={clarityRef}
          onChange={(e) => setClarityRef(e.target.value)}
          placeholder="e.g. CLR-12345"
        />
      </div>
      <div className="modal-field">
        <label>Order Value (£)</label>
        <input
          type="number" min={0} step={0.01}
          value={revenue} onChange={(e) => setRevenue(e.target.value)}
          placeholder="0.00"
        />
      </div>
      <div className="modal-field">
        <label>Note</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Quote summary, products, terms…"
        />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({
          customerId, clarityRef: clarityRef.trim(), notes: notes.trim(),
          revenue: parseFloat(revenue) || 0
        })}>Save Quote</button>
      </div>
    </Shell>
  );
}

interface UpdateQuoteProps {
  quote: Quote;
  onCancel: () => void;
  onSave: (patch: { status: QuoteStatus; revenue: number; notes: string; clarityRef: string }) => void;
}

export function QuoteUpdateModal({ quote, onCancel, onSave }: UpdateQuoteProps) {
  const [status, setStatus] = useState<QuoteStatus>(quote.status);
  const [revenue, setRevenue] = useState<string>(quote.revenue ? String(quote.revenue) : '');
  const [notes, setNotes] = useState<string>(quote.notes ?? '');
  const [clarityRef, setClarityRef] = useState<string>(quote.clarityRef);

  return (
    <Shell onClose={onCancel} maxWidth={480}>
      <div className="modal-title">Update <span>Quote</span></div>
      <div className="modal-field">
        <label>Customer</label>
        <input type="text" value={quote.customerName} readOnly style={{ opacity: 0.65, cursor: 'default' }} />
      </div>
      <div className="modal-field">
        <label>Clarity Reference</label>
        <input type="text" value={clarityRef} onChange={(e) => setClarityRef(e.target.value)} />
      </div>
      <div className="modal-field">
        <label>Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value as QuoteStatus)}>
          <option value="open">Open</option>
          <option value="won">Won ✓</option>
          <option value="lost">Lost ✗</option>
        </select>
      </div>
      <div className="modal-field">
        <label>Order Value (£)</label>
        <input
          type="number" min={0} step={0.01}
          value={revenue} onChange={(e) => setRevenue(e.target.value)}
          placeholder="0.00"
        />
      </div>
      <div className="modal-field">
        <label>Note</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({
          status,
          revenue: parseFloat(revenue) || 0,
          notes: notes.trim(),
          clarityRef: clarityRef.trim()
        })}>Save</button>
      </div>
    </Shell>
  );
}
